import React, { Component, useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import { FormattedMessage } from 'react-intl';
import { getUserAvatarUrl } from 'app/appHelpers';
import placeholderSrc from 'app/images/placeholder-avatar.svg';

import { Input, AvatarInput, Loader } from 'sa-ui';
import messages from './configurationMessages';
import messagesRegister from '../changePassword/changePasswordMessages';
import './ConfigurationForm.scss';

const shapeConfigurationSchema = {
  firstname: Yup.string(),
  lastname: Yup.string(),
  email: Yup.string(),
  oldPassword: Yup.string(),
  newPassword: Yup.string().matches(
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
    'errorStrong',
  ),
  confirmNewPassword: Yup.string().oneOf([Yup.ref('newPassword'), null], 'errorDontMatch'),
  school: Yup.string(),
};

const ConfigurationSchema = Yup.object().shape(shapeConfigurationSchema);

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  oldPassword: '',
  newPassword: '',
  confirmNewPassword: '',
  schoolName: '',
  shareMyQuestions: 0,
};

const enhanceForm = withFormik({
  mapPropsToValues: ({ values }) => ({ ...values }),
  initialValues,
  validationSchema: ConfigurationSchema,
  handleSubmit: (values, { props }) => {
    const payload = { ...values, idAccess: 7, idUser: props.user.idUser };
    props.onSubmit(payload);
  },
  enableReinitialize: true,
});

function fileToPayload(file, idUser) {
  const formData = new FormData();
  formData.append('idUser', idUser);
  formData.append('file', file);
  return formData;
}

const ConfigurationForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  handleSubmit,
  onUpload,
  onRemove,
  user,
  isLoading,
  userInfo,
  userInfoStatus,
  canEdit,
}) => {
  const [editForm, setEditForm] = useState(false);
  const [userCI, setUserCI] = useState(!!user.mdlUserGuid);
  const [required, setRequired] = useState({
    minimum: false,
    uppercase: false,
    lowercase: false,
    numbers: false,
    specialCaracter: false,
  });

  function handleEditForm() {
    setEditForm(true);
  }

  function handleCancelForm() {
    setEditForm(false);
    handleInputValues();
  }

  function handleInputValues() {
    setFieldValue('firstname', user.firstName);
    setFieldValue('lastname', user.lastName);
    setFieldValue('email', user.email);
    setFieldValue('schoolName', user.schoolName);
  }

  useEffect(() => {
    handleInputValues();
  }, []);

  useEffect(() => {
    if (userInfoStatus === 200) {
      setTimeout(() => {
        window.location.reload(false);
      }, 1000);
    }
  }, [userInfoStatus]);

  useEffect(() => {
    if (values.newPassword) {
      setRequired({
        minimum: values.newPassword.length >= 8,
        uppercase: !!values.newPassword.match(/[A-Z]/g),
        lowercase: !!values.newPassword.match(/[a-z]/g),
        numbers: !!values.newPassword.match(/\d+/g),
        specialCaracter: !!values.newPassword.match(/[!@#$%&*?]/g),
      });
    }
  }, [values]);

  return (
    <div>
      <Loader show={isLoading} />
      <div className="row">
        <div className="col-sm-3">
          {values.photo && <button className="photo-btn-close pe-7s-close" onClick={onRemove} />}
          <FormattedMessage {...messages.updatePhoto}>
            {(txt) => (
              <AvatarInput
                src={getUserAvatarUrl(values.photo)}
                placeholderSrc={placeholderSrc}
                label={txt}
                id="avatar"
                name="avatar"
                alt={values.name}
                onChange={(file) => onUpload(fileToPayload(file, values.idUser))}
              />
            )}
          </FormattedMessage>
        </div>
        <div className="col-sm-9">
          <FormattedMessage {...messages.myData}>
            {(txt) => (
              <h2 className="section-title">
                <i className="pe-7s-tools" />
                {txt}
              </h2>
            )}
          </FormattedMessage>
          <form onSubmit={handleSubmit}>
            {userInfoStatus === 200 && (
              <div className="boxMsg alert alert-success">
                <FormattedMessage {...messages.confirmedEditConfig} />
              </div>
            )}
            {userInfoStatus && userInfoStatus !== 200 && (
              <div className="boxMsg alert alert-danger">
                {userInfoStatus ? (
                  <FormattedMessage {...messages[`error${userInfoStatus}`]} />
                ) : (
                  <FormattedMessage {...messagesRegister.error503} />
                )}
              </div>
            )}
            <FormattedMessage {...messages.firstName}>
              {(txt) => (
                <div>
                  <Input
                    id="firstname"
                    name="firstname"
                    label={txt}
                    disabled={!editForm || userCI}
                    defaultValue={user.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              )}
            </FormattedMessage>
            <FormattedMessage {...messages.lastName}>
              {(txt) => (
                <div>
                  <Input
                    id="lastname"
                    name="lastname"
                    label={txt}
                    disabled={!editForm || userCI}
                    defaultValue={user.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              )}
            </FormattedMessage>
            <FormattedMessage {...messages.email}>
              {(txt) => (
                <Input
                  id="email"
                  name="email"
                  defaultValue={user.email}
                  type="email"
                  disabled
                  label={txt}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              )}
            </FormattedMessage>
            <FormattedMessage {...messages.school}>
              {(txt) => (
                <Input
                  id="schoolName"
                  name="schoolName"
                  defaultValue={user.schoolName}
                  type="text"
                  disabled
                  label={txt}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              )}
            </FormattedMessage>
            {canEdit && (
              <FormattedMessage {...messages.olderPassword}>
                {(txt) => (
                  <div>
                    <Input
                      id="oldPassword"
                      name="oldPassword"
                      type="password"
                      label={txt}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!editForm || userCI}
                    />
                  </div>
                )}
              </FormattedMessage>
            )}
            {canEdit && (
              <div className="input-password">
                <FormattedMessage {...messages.newPassword}>
                  {(txt) => (
                    <div>
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        label={txt}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!editForm || userCI}
                      />
                      {editForm &&
                        errors.newPassword === 'errorNotEqual' &&
                        touched.newPassword && <PassNotEqual />}
                      {editForm && errors.newPassword === 'errorStrong' && touched.newPassword && (
                        <PassStrongField />
                      )}
                    </div>
                  )}
                </FormattedMessage>
              </div>
            )}
            <div className="card card-password required-pass-card">
              <FormattedMessage {...messagesRegister.passCriterio} />
              <ul>
                <li className={`relative ${required.minimum ? 'checked' : ''}`}>
                  {required.minimum ? <i className="ti-check" /> : <i className="ti-close" />}
                  <FormattedMessage {...messagesRegister.caracterNumber} />
                </li>
                <li className={`relative ${required.uppercase ? 'checked' : ''}`}>
                  {required.uppercase ? <i className="ti-check" /> : <i className="ti-close" />}
                  <FormattedMessage {...messagesRegister.letterUppercase} />
                </li>
                <li className={`relative ${required.lowercase ? 'checked' : ''}`}>
                  {required.lowercase ? <i className="ti-check" /> : <i className="ti-close" />}
                  <FormattedMessage {...messagesRegister.letterLowercase} />
                </li>
                <li className={`relative ${required.numbers ? 'checked' : ''}`}>
                  {required.numbers ? <i className="ti-check" /> : <i className="ti-close" />}
                  <FormattedMessage {...messagesRegister.number} />
                </li>
                <li className={`relative ${required.specialCaracter ? 'checked' : ''}`}>
                  {required.specialCaracter ? (
                    <i className="ti-check" />
                  ) : (
                    <i className="ti-close" />
                  )}
                  <FormattedMessage {...messagesRegister.specialCaracter} /> {` ! @ # $ % & * ?)`}
                </li>
              </ul>
            </div>

            {canEdit && (
              <FormattedMessage {...messages.confirmNewPassword}>
                {(txt) => (
                  <div>
                    <Input
                      id="confirmNewPassword"
                      name="confirmNewPassword"
                      type="password"
                      label={txt}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!editForm || userCI}
                    />
                    {editForm &&
                      errors.confirmNewPassword === 'errorDontMatch' &&
                      touched.newPassword && <ConfirmPassDontMatch />}
                  </div>
                )}
              </FormattedMessage>
            )}
            <div style={{ display: !editForm ? 'none' : 'block' }}>
              <FormattedMessage {...messages.save}>
                {(txt) => (
                  <button
                    className="btn btn-primary btn-lg btn-block"
                    type="submit"
                    style={{ marginTop: '20px' }}
                    setFieldValue={('firstname', 'lastname', 'email', 'schoolName')}
                    value="submit"
                  >
                    {txt}
                  </button>
                )}
              </FormattedMessage>
              <button
                className="btn btn-outline-primary btn-lg btn-block"
                type="button"
                onClick={handleCancelForm}
              >
                <FormattedMessage {...messages.cancel} />
              </button>
            </div>
          </form>
          {canEdit && (
            <button
              className="btn btn-primary btn-lg btn-block"
              type="button"
              onClick={handleEditForm}
              style={{ display: editForm || userCI ? 'none' : 'block', marginTop: '20px' }}
            >
              <FormattedMessage {...messages.edit} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function checkError({ errors, touched, field, type, editForm }) {
  if (errors[field] && touched[field] && editForm) {
    if (type === 'border') {
      return { border: 'solid 1px red' };
    }
    if (type === 'label') {
      return { color: 'red' };
    }
  }
  return {};
}

const ConfirmPassDontMatch = () => (
  <div style={{ color: 'red', fontSize: '10px' }}>
    <FormattedMessage {...messagesRegister.confirmPassDontMatch} />
  </div>
);

const PassStrongField = () => (
  <div style={{ color: 'red', fontSize: '10px' }}>
    <FormattedMessage {...messagesRegister.passwordNotStrong} />
  </div>
);

const PassNotEqual = () => (
  <div style={{ color: 'red', fontSize: '10px' }}>
    <FormattedMessage {...messages.passwordNotEqual} />
  </div>
);

ConfigurationForm.propTypes = {};

export default enhanceForm(ConfigurationForm);
